import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import './DebuggingTimeMachine.css';

const DebuggingTimeMachine = ({ userId }) => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [recording, setRecording] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [code, setCode] = useState('');
  const [timeline, setTimeline] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const playbackIntervalRef = useRef(null);

  useEffect(() => {
    loadSessions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (isPlaying && recording && currentIndex < recording.length) {
      playbackIntervalRef.current = setInterval(() => {
        playNextKeystroke();
      }, 50 / playbackSpeed);
    } else {
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
      }
      if (currentIndex >= recording?.length) {
        setIsPlaying(false);
      }
    }
    return () => {
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, currentIndex, playbackSpeed, recording]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/ghost/user/${userId}/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data);
    } catch (err) {
      console.error('Error loading sessions:', err);
      setError('Failed to load coding sessions');
    } finally {
      setLoading(false);
    }
  };

  const loadRecording = async (sessionId) => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/ghost/session/${sessionId}/replay`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedSession(sessions.find(s => s.id === sessionId));
      setRecording(response.data.events);
      setTimeline(buildTimeline(response.data.events));
      setCode('');
      setCurrentIndex(0);
      setIsPlaying(false);
    } catch (err) {
      console.error('Error loading recording:', err);
      setError('Failed to load session recording');
    } finally {
      setLoading(false);
    }
  };

  const buildTimeline = (events) => {
    const timelineEvents = [];
    let codeSnapshot = '';
    
    events.forEach((event, index) => {
      if (event.type === 'keypress') {
        codeSnapshot += event.character;
      } else if (event.type === 'delete') {
        codeSnapshot = codeSnapshot.slice(0, -1);
      } else if (event.type === 'error' || event.type === 'bug' || event.type === 'fix') {
        timelineEvents.push({
          index,
          type: event.type,
          timestamp: event.timestamp,
          description: event.description || getEventDescription(event.type),
          codeSnapshot
        });
      }
    });
    
    return timelineEvents;
  };

  const getEventDescription = (type) => {
    switch (type) {
      case 'error': return 'Compilation error detected';
      case 'bug': return 'Potential bug introduced';
      case 'fix': return 'Bug fix applied';
      default: return 'Event occurred';
    }
  };

  const playNextKeystroke = () => {
    if (!recording || currentIndex >= recording.length) {
      setIsPlaying(false);
      return;
    }

    const event = recording[currentIndex];
    
    if (event.type === 'keypress') {
      setCode(prev => prev + event.character);
    } else if (event.type === 'delete') {
      setCode(prev => prev.slice(0, -1));
    } else if (event.type === 'clear') {
      setCode('');
    }
    
    setCurrentIndex(prev => prev + 1);
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleStop = () => {
    setIsPlaying(false);
    setCurrentIndex(0);
    setCode('');
    setSelectedEvent(null);
  };

  const handleSpeedChange = (speed) => {
    setPlaybackSpeed(speed);
  };

  const jumpToEvent = (eventIndex) => {
    setIsPlaying(false);
    setCurrentIndex(eventIndex);
    
    // Reconstruct code up to this point
    let reconstructedCode = '';
    for (let i = 0; i < eventIndex && i < recording.length; i++) {
      const event = recording[i];
      if (event.type === 'keypress') {
        reconstructedCode += event.character;
      } else if (event.type === 'delete') {
        reconstructedCode = reconstructedCode.slice(0, -1);
      } else if (event.type === 'clear') {
        reconstructedCode = '';
      }
    }
    setCode(reconstructedCode);
    
    const timelineEvent = timeline.find(e => e.index === eventIndex);
    setSelectedEvent(timelineEvent);
  };

  const handleSliderChange = (e) => {
    const newIndex = parseInt(e.target.value);
    jumpToEvent(newIndex);
  };

  const formatTime = (milliseconds) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'error': return '❌';
      case 'bug': return '🐛';
      case 'fix': return '✅';
      default: return '📍';
    }
  };

  const getEventColor = (type) => {
    switch (type) {
      case 'error': return 'border-red-500 bg-red-50';
      case 'bug': return 'border-yellow-500 bg-yellow-50';
      case 'fix': return 'border-green-500 bg-green-50';
      default: return 'border-blue-500 bg-blue-50';
    }
  };

  return (
    <div className="time-machine-container">
      {!selectedSession ? (
        <div className="sessions-list">
          <div className="header">
            <h2>⏰ Debugging Time Machine</h2>
            <p className="subtitle">Travel through time to find when bugs were introduced</p>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading your coding sessions...</p>
            </div>
          ) : sessions.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">⏰</div>
              <h3>No Recorded Sessions</h3>
              <p>Start coding to record your first session!</p>
              <p className="hint">Your coding sessions are automatically recorded for debugging</p>
            </div>
          ) : (
            <div className="sessions-grid">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  className="session-card"
                  onClick={() => loadRecording(session.id)}
                >
                  <div className="session-header">
                    <span className="session-icon">💾</span>
                    <span className="session-title">{session.title || 'Coding Session'}</span>
                  </div>
                  <div className="session-info">
                    <div className="info-row">
                      <span className="info-label">Language:</span>
                      <span className="info-value">{session.language || 'Java'}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Duration:</span>
                      <span className="info-value">{formatTime(session.duration || 0)}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Quality:</span>
                      <span className="info-value">
                        {session.codeQualityScore ? `${session.codeQualityScore}/100` : 'N/A'}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Date:</span>
                      <span className="info-value">
                        {new Date(session.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <button className="view-button">
                    ⏰ View Timeline
                  </button>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="playback-container">
          {/* Header with Back Button */}
          <div className="playback-header">
            <button className="back-button" onClick={() => setSelectedSession(null)}>
              ← Back to Sessions
            </button>
            <h3>{selectedSession.title || 'Coding Session'}</h3>
            <span className="session-date">
              {new Date(selectedSession.createdAt).toLocaleString()}
            </span>
          </div>

          {/* Code Display */}
          <div className="code-display">
            <div className="code-display-header">
              <span>💻 Code at {formatTime((recording?.[currentIndex]?.timestamp || 0))}</span>
              <span className="progress">
                Event {currentIndex}/{recording?.length || 0}
              </span>
            </div>
            <pre className="code-content">
              <code>{code || '// Code will appear here as you replay the session'}</code>
            </pre>
          </div>

          {/* Timeline Events */}
          {timeline.length > 0 && (
            <div className="timeline-events">
              <h4>📍 Key Events</h4>
              <div className="events-grid">
                {timeline.map((event, idx) => (
                  <div
                    key={idx}
                    className={`event-marker ${getEventColor(event.type)} ${
                      selectedEvent?.index === event.index ? 'selected' : ''
                    }`}
                    onClick={() => jumpToEvent(event.index)}
                  >
                    <span className="event-icon">{getEventIcon(event.type)}</span>
                    <div className="event-details">
                      <span className="event-type">{event.type.toUpperCase()}</span>
                      <span className="event-desc">{event.description}</span>
                      <span className="event-time">{formatTime(event.timestamp)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Playback Controls */}
          <div className="playback-controls">
            {/* Timeline Slider */}
            <div className="timeline-slider">
              <input
                type="range"
                min="0"
                max={recording?.length || 0}
                value={currentIndex}
                onChange={handleSliderChange}
                className="slider"
              />
              <div className="timeline-markers">
                {timeline.map((event, idx) => (
                  <div
                    key={idx}
                    className="timeline-marker"
                    style={{
                      left: `${(event.index / (recording?.length || 1)) * 100}%`
                    }}
                    title={event.description}
                  >
                    {getEventIcon(event.type)}
                  </div>
                ))}
              </div>
            </div>

            {/* Control Buttons */}
            <div className="control-buttons">
              <button
                className="control-btn"
                onClick={() => jumpToEvent(Math.max(0, currentIndex - 10))}
                disabled={currentIndex === 0}
              >
                ⏪ -10
              </button>
              <button
                className="control-btn primary"
                onClick={handlePlayPause}
              >
                {isPlaying ? '⏸ Pause' : '▶️ Play'}
              </button>
              <button
                className="control-btn"
                onClick={handleStop}
              >
                ⏹ Stop
              </button>
              <button
                className="control-btn"
                onClick={() => jumpToEvent(Math.min(recording?.length || 0, currentIndex + 10))}
                disabled={currentIndex >= (recording?.length || 0)}
              >
                +10 ⏩
              </button>
            </div>

            {/* Speed Controls */}
            <div className="speed-controls">
              <span className="speed-label">Speed:</span>
              {[0.25, 0.5, 1, 2, 4].map((speed) => (
                <button
                  key={speed}
                  className={`speed-btn ${playbackSpeed === speed ? 'active' : ''}`}
                  onClick={() => handleSpeedChange(speed)}
                >
                  {speed}x
                </button>
              ))}
            </div>
          </div>

          {/* Selected Event Details */}
          {selectedEvent && (
            <div className={`event-details-panel ${getEventColor(selectedEvent.type)}`}>
              <h4>
                {getEventIcon(selectedEvent.type)} Event Details
              </h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Type:</span>
                  <span className="detail-value">{selectedEvent.type.toUpperCase()}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Time:</span>
                  <span className="detail-value">{formatTime(selectedEvent.timestamp)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Description:</span>
                  <span className="detail-value">{selectedEvent.description}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DebuggingTimeMachine;


